export const clipPlaneCreateEventName = 'clip-plane-create-custom-event'

type Vector3Data = {
  x: number
  y: number
  z: number
}

export type ClipPlaneTarget = 'all' | 'gltf'
export type ClipPlaneRenderMode = 'solid-plane' | 'dashed-outline'
export type ClipPlaneMode = 'half-space' | 'bracket'

export type ClipPlaneCreateEventData = {
  normal: Vector3Data
  point: Vector3Data
  /**
   * Clipping geometry to build. 'half-space' clips everything on one side; 'bracket'
   * ("Schnittklammer") keeps a thin slab around the plane. Defaults to 'half-space'.
   */
  mode?: ClipPlaneMode
  /**
   * Explicit id for the created plane. When omitted an id is generated. Pass a known id when the
   * caller needs to recognize this plane later (e.g. via the scene store's clip plane ids).
   */
  id?: string
  /** When true, this plane replaces all existing clip planes instead of adding to them. */
  replaceExisting?: boolean
  /**
   * Internal routing for which model is clipped. This is not exposed to the user.
   * - 'all': clips everything (default)
   * - 'gltf': clips only the IFC GLTF mesh
   * - 'planar': clips only the planar model (everything except the GLTF mesh)
   */
  target?: ClipPlaneTarget
  /** Optional id used to keep related clip planes moving together. */
  linkGroupId?: string
  /** Controls the visual helper rendered for this plane. */
  renderMode?: ClipPlaneRenderMode
}

export type ClipPlaneCreateEvent = CustomEvent<ClipPlaneCreateEventData>

function subscribeClipPlaneCreateEvent(handler: (event: ClipPlaneCreateEvent) => void) {
  document.addEventListener(clipPlaneCreateEventName, handler as unknown as EventListener)
}

function unsubscribeClipPlaneCreateEvent(handler: (event: ClipPlaneCreateEvent) => void) {
  document.removeEventListener(clipPlaneCreateEventName, handler as unknown as EventListener)
}

function publishClipPlaneCreateEvent(data: ClipPlaneCreateEventData) {
  const event = new CustomEvent<ClipPlaneCreateEventData>(clipPlaneCreateEventName, {
    detail: data,
  })
  document.dispatchEvent(event)
}

export {
  publishClipPlaneCreateEvent,
  subscribeClipPlaneCreateEvent,
  unsubscribeClipPlaneCreateEvent,
}
